import { useState, useEffect } from 'react';
import { Routes, Route, BrowserRouter as Router, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Discovery from './components/Discovery';
import Details from './components/details';
import Main from './Main';
import Layer from '@arcgis/core/layers/Layer';
import DerivationService from './services/DerivationService';
import esriConfig from '@arcgis/core/config';
import * as geometryJsonUtils from '@arcgis/core/geometry/support/jsonUtils';
import { registerMuiLicense } from './config/muiLicense.ts';
import Login from './Login';
import UserService from './services/UserService';
import FieldNames from './defaults/FieldNames.json';
import { useDispatch, useSelector } from 'react-redux';
import {
    setConfig,
    setNifcToken,
    setRootServiceUrl,
    setViewRootServiceUrl,
    setSummaryViewRootServiceUrl,
} from './redux/slices/configurationSlice';
import './App.css';
import AuthManager from './components/esri/auth/AuthManager';
import {
    getLegacyModifiedAttributes,
    getWFFTSModifiedAttributes,
    isFeaturePostFire,
} from './utils/FeatureHelper';
import UserAdminDialog from './components/popover/UserAdminDialog/UserAdminDialog';
import BudgetManagementDialog from './components/popover/BudgetManagementDialog/BudgetManagementDialog';
import CarryoverManagementDialog from './components/popover/CarryoverManagementDialog/CarryoverManagementDialog';
import DownloadDialog from './components/popover/DownloadDialog/DownloadDialog';
import logo from './images/IFPRS_Logo.png';
import { Dialog } from '@mui/material';
import ErrorService from './services/ErrorService';
import TargetManagementDialog from './components/popover/TargetManagementDialog/TargetManagementDialog';
import { ResetFeatureFundingLinesByFeature } from './utils/FundingHelper';
import { getSaveableDate } from './utils/DateHelper';
import { SummaryReportsView } from './components/SummaryReports';
import BulkApprovalDialog from './components/popover/BulkApprovalDialog/BulkApprovalDialog';
import { validateCacheByProfile } from './utils/AdminHelper';
import packageInfo from '../package.json';
import { setDataset } from './redux/slices/filterSlice';
import AdminOverridesPanel from './components/adminOverrides/AdminOverridesPanel';
import {legacyRedirects, routes, Redirect, flatRoutes} from './config/route-config';

import { setShowGlobalLoader, setUserInfo, setUserRoleInfo } from '@store/slices/globalContextSlice';
import BulkEdit from './components/BulkEdit/BulkEdit';
import { setDerivationErrorOpen, setDerivationError } from './redux/slices/errorSlice';
import {setFeature, setProject} from './redux/slices/detailsSlice';
import RouteTreatmentSync from "./components/shared/RouteTreatmentSync";

window.authManager = new AuthManager();

registerMuiLicense();

export default function App() {
    /* Redux */
    const dispatch = useDispatch();

    /* Config */
    const basename = import.meta.env.BASE_URL; // TODO - EVAL: Why is this imported here? Move to config?

    /* Local State - Mostly drilled - TODO: Elevate to store */
    const [isEditingTreatment, setIsEditingTreatment] = useState(false);
    const [listOpen, setListOpen] = useState(true);
    const [userCredential, setUserCredential] = useState(null);
    const [isUserFound, setIsUserFound] = useState(false);
    const [isConfigLoaded, setIsConfigLoaded] = useState(false);
    const [isInReadWriteGroup, setIsInReadWriteGroup] = useState(false);
    const [isCheckingCredentials, setIsCheckingCredentials] = useState(true);
    const [mapSnackbarMessage, setMapSnackbarMessage] = useState('');
    const [mapSnackbarSeverity, setMapSnackbarSeverity] = useState('success');
    const [mapSnackbarOpen, setMapSnackbarOpen] = useState(false);
    const [isError, setIsError] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState('');

    const showMap = useSelector((state) => state.displayState.showMap);
    const feature = useSelector((state) => state.details.feature);
    const config = useSelector((state) => state.configuration.config);
    const userInfo = useSelector((state) => state.globalContext.userInfo);
    const userRoleInfo = useSelector((state) => state.globalContext.userRoleInfo);
    const isProject = useSelector((state) => state.details.isProject);

    useEffect(() => {
        esriConfig.portalUrl = 'https://nifc.maps.arcgis.com'; //TODO: Unhardcode this

        window.api.getAppConfig().then((response) => {
            dispatch(setConfig(response));
            setIsConfigLoaded(true);
            //DEV NOTE: Do NOT Put any Queries in here to external services -> It will interrupt the query to Auth and if it fails Auth thinks that the Login is failing
            //This will cause and infinite loop -> Crashes entire application
        });
    }, []);

    useEffect(() => {
        if (userInfo) {
            const isUserPostfire =
                userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes;
            const isUserFuels = userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes;
            const tempDataset =
                isUserPostfire && isUserFuels
                    ? 'FUELS'
                    : isUserPostfire && !isUserFuels
                      ? 'POSTFIRE'
                      : 'FUELS';
            dispatch(setDataset(tempDataset));
        }
    }, [userInfo]);

    useEffect(() => {
        if (!isConfigLoaded || !config) {
            return;
        }
        console.log('IFPRS Version ' + packageInfo.version + ' is starting up...');

        window.authManager = new AuthManager(
            config.appId[config.env],
            config.portalUrl,
            config.loginWithPopup
        );

        window.authManager.startup().then(
            () => {
                window.authManager.checkLogin().then(
                    (response) => {
                        const user = response.user;
                        const token = response.token;
                        dispatch(setNifcToken(token));
                        setUserCredential(user);
                        const sharingUrl =
                            'https://nifc.maps.arcgis.com/sharing/rest/content/items';
                        const proxyUrl = 'https://utility.arcgis.com';

                        //set the views url
                        Layer.fromPortalItem({
                            portalItem: {
                                id: config.targetTreatmentView[config.env].portalItem.id,
                            },
                        }).then(
                            (layer) => {
                                layer.load().then(() => {
                                    dispatch(setViewRootServiceUrl(layer.portalItem.url));
                                });
                            },
                            (error) => {
                                console.log(error);
                            }
                        );

                        //set the summary views url
                        Layer.fromPortalItem({
                            portalItem: {
                                id: config.summaryAttributesView[config.env].portalItem.id,
                            },
                        }).then(
                            (layer) => {
                                layer.load().then(() => {
                                    dispatch(setSummaryViewRootServiceUrl(layer.portalItem.url));
                                });
                            },
                            (error) => {
                                console.log(error);
                            }
                        );

                        // Creates a layer from a Portal layer item id
                        Layer.fromPortalItem({
                            portalItem: {
                                id: config.actionAttributes[config.env].portalItem.id,
                            },
                        }).then(
                            (layer) => {
                                layer.load().then(
                                    () => {
                                        dispatch(setRootServiceUrl(layer.portalItem.url));
                                        window.api
                                            .getUserGroups(user.username, token)
                                            .then((response) => {
                                                const inReadWriteGroup =
                                                    response.groups.filter((group) => {
                                                        return (
                                                            group.id ===
                                                            config.accessGroupId[config.env]
                                                        );
                                                    }).length === 1;
                                                setIsInReadWriteGroup(inReadWriteGroup);
                                                const currentlyNonProduction =
                                                    config.env !== 'prod';
                                                let inNoGroups = !inReadWriteGroup;
                                                if (currentlyNonProduction && inNoGroups) {
                                                    const inProductionReadWriteGroup =
                                                        response.groups.filter((group) => {
                                                            return (
                                                                group.id ===
                                                                config.accessGroupId['prod']
                                                            );
                                                        }).length === 1;
                                                    if (inProductionReadWriteGroup) {
                                                        setIsInReadWriteGroup(
                                                            inProductionReadWriteGroup
                                                        );
                                                        inNoGroups = false;
                                                    }
                                                }

                                                if (inNoGroups) {
                                                    dispatch(setUserInfo(null));
                                                    dispatch(setUserRoleInfo([]));
                                                    setIsUserFound(false);
                                                } else {
                                                    UserService.getUserByNifcUsername(
                                                        layer.portalItem.url,
                                                        user.username,
                                                        config
                                                    )
                                                        .then((response) => {
                                                            if (response && response.length > 0) {
                                                                dispatch(setUserInfo(response[0]));
                                                                validateCacheByProfile(response[0]);
                                                                if (response[0]) {
                                                                    setIsUserFound(true);
                                                                    UserService.getUserRoles(
                                                                        layer.portalItem.url,
                                                                        response[0][
                                                                            FieldNames.userFields.id
                                                                        ],
                                                                        config
                                                                    )
                                                                        .then((userRoles) => {
                                                                            setIsCheckingCredentials(
                                                                                false
                                                                            );
                                                                            if (
                                                                                userRoles &&
                                                                                userRoles.length > 0
                                                                            ) {
                                                                                dispatch(setUserRoleInfo(userRoles));
                                                                            } else {
                                                                                dispatch(setUserRoleInfo([]));
                                                                            }
                                                                        })
                                                                        .catch((error) => {
                                                                            ErrorService.addErrorLogEntry(
                                                                                error,
                                                                                'SYSTEM'
                                                                            );
                                                                        });
                                                                } else {
                                                                    setIsUserFound(false);
                                                                }

                                                                let user = {...response[0]};
                                                                user[
                                                                    FieldNames.userFields.lastLogin
                                                                ] = getSaveableDate(new Date());
                                                                UserService.updateUser(
                                                                    layer.portalItem.url,
                                                                    token,
                                                                    user,
                                                                    config
                                                                )
                                                                    .then((updatedResp) => {
                                                                        if (
                                                                            updatedResp &&
                                                                            updatedResp.updateResults &&
                                                                            updatedResp
                                                                                .updateResults
                                                                                .length > 0
                                                                        ) {
                                                                            // TODO - EVAL: No action at this time?
                                                                        }
                                                                    })
                                                                    .catch((error) => {
                                                                        ErrorService.addErrorLogEntry(
                                                                            error,
                                                                            'SYSTEM',
                                                                        );
                                                                    });
                                                            } else {
                                                                setIsUserFound(false); //TODO: We need to throw something in the UI here...
                                                            }
                                                        })
                                                        .catch((error) => {
                                                            ErrorService.addErrorLogEntry(
                                                                error,
                                                                'SYSTEM',
                                                            );
                                                        });
                                                }
                                            });
                                    },
                                    (error) => {
                                        setIsUserFound(false);
                                        setIsError(true);
                                        if (error) {
                                            setLoginErrorMessage(error.message);
                                            ErrorService.addErrorLogEntry(error, 'SYSTEM');
                                        }
                                    }
                                );
                            },
                            (error) => {
                                setIsUserFound(false);
                                setIsError(true);
                                if (error) {
                                    setLoginErrorMessage(error.message);
                                    ErrorService.addErrorLogEntry(error, 'SYSTEM');
                                }
                            }
                        );
                    },
                    (error) => {
                        setIsUserFound(false);
                        if (error && error !== 'User is not logged in') {
                            setIsError(true);
                            if (error) {
                                setLoginErrorMessage(error.message);
                                ErrorService.addErrorLogEntry(error, 'SYSTEM');
                            }
                        }
                    }
                );
            },
            (error) => {
                setIsUserFound(false);
                setIsError(true);
                if (error) {
                    setLoginErrorMessage(error.message);
                    ErrorService.addErrorLogEntry(error, 'SYSTEM');
                }
            }
        );
    }, [isConfigLoaded, config]);

    const logout = () => {
        setUserCredential(null);
        dispatch(setUserInfo(null));
        window.authManager.logout();
    };

    return isError && loginErrorMessage !== 'User is not logged in' ? (
        <Dialog open={true}>
            <div className='login-error__message-container'>
                <img src={logo} alt='IFPRS' />
                <p>Error: {loginErrorMessage}</p>
                <p>IFPRS has encountered an error and cannot proceed.</p>
                <p>You may try reloading your browser.</p>
            </div>
        </Dialog>
    ) : userInfo && isUserFound && isInReadWriteGroup && userRoleInfo && userRoleInfo.length > 0 ? (
        <div className='app-content' style={{ display: showMap ? 'flex' : 'block' }}>
            <Router basename={basename}>
                <RouteTreatmentSync />

                <Header
                    isEditingTreatment={isEditingTreatment}
                    setIsEditingTreatment={setIsEditingTreatment}
                    logout={logout}
                    mapSnackbarMessage={mapSnackbarMessage}
                    setMapSnackbarMessage={setMapSnackbarMessage}
                    mapSnackbarOpen={mapSnackbarOpen}
                    setMapSnackbarOpen={setMapSnackbarOpen}
                    mapSnackbarSeverity={mapSnackbarSeverity}
                    setMapSnackbarSeverity={setMapSnackbarSeverity}
                />

                <Routes>
                    {legacyRedirects.flatMap(({ from, to }) =>
                        from.map((path) => (
                            <Route key={path} path={path} element={<Redirect to={to} />} />
                        ))
                    )}

                     {/* TODO: Extract route-based logic from Details component, potentially passing
                          in using flatRoutes' upstream config object. */}
                     {flatRoutes.map((path) => (
                        <Route
                            key={path}
                            path={path}
                            element={<Details />}
                        />
                    ))}

                    <Route
                        path='/home'
                        element={
                            <Discovery
                                listOpen={listOpen}
                                setListOpen={setListOpen}
                                setIsEditingTreatment={setIsEditingTreatment}
                            />
                        }
                    />

                    <Route
                        path='/admin/users'
                        element={<UserAdminDialog />}
                    />
                    <Route
                        path='/admin/overrides'
                        element={<AdminOverridesPanel />}
                    />
                    <Route
                        path='/workflow/bulkapproval'
                        element={<BulkApprovalDialog />}
                    />
                    <Route
                        path='/workflow/budget'
                        element={<BudgetManagementDialog />}
                    />
                    <Route
                        path='/workflow/carryover'
                        element={<CarryoverManagementDialog />}
                    />
                    <Route
                        path='/workflow/downloads'
                        element={<DownloadDialog />}
                    />
                    <Route
                        path='/workflow/targets'
                        element={<TargetManagementDialog />}
                    />
                    <Route
                        path='/workflow/reporting'
                        element={<SummaryReportsView />}
                    />
                    <Route
                        path='/workflow/bulkedit'
                        element={
                            userRoleInfo?.length > 0 &&
                            (userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.department ||
                                userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.agency)
                                ? <BulkEdit />
                                : <Navigate to='/home' replace />
                        }
                    />
                </Routes>

                {/* -- This Component contains the actual map -- */}
                <Main
                    listOpen={listOpen}
                    isConfigLoaded={isConfigLoaded}
                    mapSnackbarMessage={mapSnackbarMessage}
                    setMapSnackbarMessage={setMapSnackbarMessage}
                    mapSnackbarOpen={mapSnackbarOpen}
                    setMapSnackbarOpen={setMapSnackbarOpen}
                    mapSnackbarSeverity={mapSnackbarSeverity}
                    setMapSnackbarSeverity={setMapSnackbarSeverity}
                />
            </Router>
        </div>
    ) : (
        <Login
            userCredential={userCredential}
            isUserFound={isUserFound}
            logout={logout}
            isInReadWriteGroup={isInReadWriteGroup}
            isCheckingCredentials={isCheckingCredentials}
        />
    );
}
